/**
 * UploadModals — generic Upload + IFC/RVT + E57 + GEOTIFF + Asset (CSV) upload modals for SitesPage.
 *
 * JSX moved verbatim from src/components/SitesPage.tsx (SitesPage split 3/3).
 * Rendered DOM is byte-equivalent (classNames, text, conditional structure, keys unchanged).
 * All page state/handlers arrive as props; the interface + SitesPage invocation are
 * generated from a single prop list so they cannot drift.
 */
import React from 'react';
import { Asset } from '../../services/AssetService';
import { FileUploadProgress } from '../../services/FileService';

interface UploadModalsProps {
  analyzingIFC: boolean;
  getSelectedFile: (assetId: string, fileType: string) => File | null;
  isUploading: boolean;
  selectedAsset: Asset | null;
  showE57UploadModal: boolean;
  showGEOTIFFUploadModal: boolean;
  showIFCUploadModal: boolean;
  showPOIUploadModal: boolean;
  showUploadModal: boolean;
  t: (key: string, fallback?: string) => string;
  uploadFileType: string;
  uploadProgress: { [key: string]: FileUploadProgress };
  setIfcUploadedFile: React.Dispatch<React.SetStateAction<File | null>>;
  setSelectedCompanionFiles: React.Dispatch<React.SetStateAction<{ [assetId: string]: File[] }>>;
  setSelectedFile: (assetId: string, fileType: string, file: File | null) => void;
  setShowE57UploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowGEOTIFFUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowIFCUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPOIUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setShowUploadModal: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadFileType: React.Dispatch<React.SetStateAction<string>>;
  handleFileUpload: () => Promise<void>;
  handleSpecificFileUpload: (fileType: string) => Promise<void>;
}

const UploadModals: React.FC<UploadModalsProps> = ({
  analyzingIFC,
  getSelectedFile,
  isUploading,
  selectedAsset,
  showE57UploadModal,
  showGEOTIFFUploadModal,
  showIFCUploadModal,
  showPOIUploadModal,
  showUploadModal,
  t,
  uploadFileType,
  uploadProgress,
  setIfcUploadedFile,
  setSelectedCompanionFiles,
  setSelectedFile,
  setShowE57UploadModal,
  setShowGEOTIFFUploadModal,
  setShowIFCUploadModal,
  setShowPOIUploadModal,
  setShowUploadModal,
  setUploadFileType,
  handleFileUpload,
  handleSpecificFileUpload,
}) => {
  return (
    <>
      {/* Upload File Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>{t('uploadFile', 'Upload File')}</h2>
              <select
                value={uploadFileType}
                onChange={(e) => setUploadFileType(e.target.value)}
                className="form-select"
              >
                <option value="ifc">IFC Model</option>
                <option value="e57">E57 Panorama</option>
                <option value="dwg">DWG Drawing</option>
                <option value="poi">Assets (CSV)</option>
              </select>
              <input
                type="file"
                value=""
                onChange={(e) => selectedAsset && setSelectedFile(selectedAsset.id, uploadFileType, e.target.files?.[0] || null)}
                className="form-input"
              />
              <div className="modal-actions">
                <button onClick={() => setShowUploadModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={handleFileUpload}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, uploadFileType)}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Individual File Upload Modals */}

      {/* IFC/RVT Upload Modal */}
      {showIFCUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Upload Model</h2>
              <p>Upload a 3D BIM model in IFC or Revit (RVT) format for 3D viewing.</p>
              <div className="upload-info">
                <ul>
                  <li>✅ Supports IFC and Revit (RVT) files</li>
                  <li>✅ 3D viewing with element selection</li>
                  <li>✅ Property inspection and search</li>
                </ul>
              </div>
              <input
                type="file"
                accept=".ifc,.rvt"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (selectedAsset && file) {
                    setSelectedFile(selectedAsset.id, 'ifc', file);
                    setIfcUploadedFile(file);
                  }
                }}
                className="form-input"
                disabled={analyzingIFC || isUploading}
              />

              {/* Upload Progress Bar */}
              {selectedAsset && uploadProgress[selectedAsset.id] && (
                <div className="upload-progress">
                  <div className="progress-info">
                    <span className="progress-status">{uploadProgress[selectedAsset.id].message}</span>
                    <span className="progress-percentage">{uploadProgress[selectedAsset.id].progress}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress[selectedAsset.id].progress}%` }}
                    ></div>
                  </div>
                  <div className="processing-note">
                    <small>Uploading model file to server...</small>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => setShowIFCUploadModal(false)}
                  className="btn btn-secondary"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const file = selectedAsset ? getSelectedFile(selectedAsset.id, 'ifc') : null;
                    const fileType = file?.name.toLowerCase().endsWith('.rvt') ? 'rvt' : 'ifc';
                    handleSpecificFileUpload(fileType);
                  }}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, 'ifc') || isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Upload Model'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* E57 Upload Modal */}
      {showE57UploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Upload E57 Source</h2>
              <p>Upload an E57 file to add another panorama source to this site.</p>
              <div className="upload-info">
                <ul>
                  <li>✅ Panoramic images will be extracted automatically</li>
                  <li>✅ Camera positions and metadata will be preserved</li>
                  <li>✅ Existing E57 sources on this site will remain available</li>
                  <li>✅ Individual panoramas will be viewable in the list</li>
                  <li>ℹ️ The original E57 file will not be stored</li>
                </ul>
              </div>
              <input
                type="file"
                accept=".e57"
                onChange={(e) => selectedAsset && setSelectedFile(selectedAsset.id, 'e57', e.target.files?.[0] || null)}
                className="form-input"
                disabled={isUploading}
              />

              {/* Progress Bar */}
              {selectedAsset && uploadProgress[selectedAsset.id] && (
                <div className="upload-progress">
                  <div className="progress-info">
                    <span className="progress-status">{uploadProgress[selectedAsset.id].message}</span>
                    <span className="progress-percentage">{uploadProgress[selectedAsset.id].progress}%</span>
                  </div>
                  <div className="progress-bar">
                    <div
                      className="progress-fill"
                      style={{ width: `${uploadProgress[selectedAsset.id].progress}%` }}
                    ></div>
                  </div>
                  {uploadProgress[selectedAsset.id].status === 'processing' && (
                    <div className="processing-note">
                      <small>⚡ Extracting panoramic images from E57 file...</small>
                    </div>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button
                  onClick={() => setShowE57UploadModal(false)}
                  className="btn btn-secondary"
                  disabled={isUploading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSpecificFileUpload('e57')}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, 'e57') || isUploading}
                >
                  {isUploading ? 'Processing...' : 'Add E57 Source'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GEOTIFF Upload Modal - Replacing DWG */}
      {showGEOTIFFUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Upload 2D Map/Image</h2>
              <p>Upload 2D imagery for mapping and visualization. Supports GEOTIFF, TIFF, PNG, and JPEG formats.</p>
              <div className="upload-info">
                <ul>
                  <li>✅ GEOTIFF: Geographic coordinate systems (requires conversion)</li>
                  <li>✅ TIFF/PNG/JPEG: Direct image display</li>
                  <li>✅ High-resolution imagery support</li>
                  <li>ℹ️ File size limit: 100MB recommended</li>
                </ul>
              </div>
              <div className="file-upload-group">
                <div className="main-file-upload">
                  <label>Main Image File:</label>
                  <input
                    type="file"
                    accept=".tif,.tiff,.geotiff,.png,.jpg,.jpeg"
                    onChange={(e) => selectedAsset && setSelectedFile(selectedAsset.id, 'geotiff', e.target.files?.[0] || null)}
                    className="form-input"
                  />
                </div>
                <div className="companion-file-upload">
                  <label>Companion Files (Optional):</label>
                  <input
                    type="file"
                    accept=".tfw,.jgw,.pgw,.gfw,.wld"
                    multiple
                    onChange={(e) => {
                      if (selectedAsset && e.target.files) {
                        const filesArray = Array.from(e.target.files);
                        setSelectedCompanionFiles(prev => ({
                          ...prev,
                          [selectedAsset.id]: filesArray
                        }));
                      }
                    }}
                    className="form-input"
                  />
                  <small className="help-text">
                    World files (.tfw, .jgw, .pgw) for accurate geographic positioning
                  </small>
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={() => setShowGEOTIFFUploadModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => handleSpecificFileUpload('geotiff')}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, 'geotiff')}
                >
                  Upload Map/Image
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DWG Upload Modal - COMMENTED OUT - Using GEOTIFF instead
      {showDWGUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Upload DWG Drawings</h2>
              <p>Upload 2D technical drawings in DWG format.</p>
              <input
                type="file"
                accept=".dwg"
                onChange={(e) => selectedAsset && setSelectedFile(selectedAsset.id, 'dwg', e.target.files?.[0] || null)}
                className="form-input"
              />
              <div className="modal-actions">
                <button onClick={() => setShowDWGUploadModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => handleSpecificFileUpload('dwg')}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, 'dwg')}
                >
                  Upload DWG
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      */}

      {/* Asset Upload Modal */}
      {showPOIUploadModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-content">
              <h2>Upload Assets</h2>
              <p>Upload asset data in CSV format. File should contain coordinates, titles, and metadata.</p>
              <input
                type="file"
                accept=".csv"
                onChange={(e) => selectedAsset && setSelectedFile(selectedAsset.id, 'poi', e.target.files?.[0] || null)}
                className="form-input"
              />
              <div className="modal-actions">
                <button onClick={() => setShowPOIUploadModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => handleSpecificFileUpload('poi')}
                  className="btn btn-primary"
                  disabled={!selectedAsset || !getSelectedFile(selectedAsset.id, 'poi')}
                >
                  Upload Assets
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadModals;
